import { useMutation, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import {
  DEFAULT_FEASIBILITY_LENGTH_OF_INTERVIEW,
  FEASIBILITY_POLL_INTERVAL
} from '../../../../constants/feasibility'
import { CreateSurveyOrderV2MutationVariables } from '../../../../data/gql-gen/dashboard/graphql'
import {
  CREATE_SURVEY_ORDER_V2,
  CreateSurveyOrderData
} from '../../../../data/gql/order/mutations/createSurveyOrder'
import { GET_FIELDWORK } from '../../../../data/gql/questionnaire/queries/fieldwork'
import { GET_COST_QUOTE } from '../../../../data/gql/questionnaire/queries/getCostQuote'
import { GET_FEASIBILITY_ESTIMATE } from '../../../../data/gql/questionnaire/queries/getFeasibilityEstimate'
import { LoggerErrorType } from '../../../../data/logger'
import { useUpdateFieldworkV2 } from '../../../../hooks/audience/createOrUpdateFieldworkV2'
import {
  calculateTotalCost,
  captureApolloError
} from '../../../../utils/HelperFunctions'
import {
  PanelSupplier,
  convertAudienceType
} from '../../../../utils/audienceHelper'
import { getLaunchErrorDetails } from '../../../../utils/launchErrorDetails'
import { getSurveyLiveConfig } from '../../../../utils/surveyLiveConfig'
import {
  getMaxDateBetweenDefaultAndEndDate,
  getMaxEstimatedDeliveryDays,
  getStartAndEndDatesAdjusted,
  getStartAndEndDatesFormatted
} from '../../Survey/Confirmation/Confirmation.utils'

const DEFAULT_ESTIMATED_DELIVERY_TIME = 2

export interface MarketLaunchSummary {
  goLiveDate: string
  estimatedDeliveryDays: number
  completionDate: string
  costPerComplete?: number
  completes?: number
  totalCost?: number
}

/**
 * Launch data + order placement for a single market, mirroring the single-survey
 * Confirmation page (`Survey/Confirmation/index.tsx`). The Confirmation page
 * reads survey/project from the route; here they're passed explicitly and the
 * underlying queries are issued directly, so this works for any market without
 * routing/navigation.
 */
export const useMarketLaunch = (
  projectId: string,
  surveyId: string,
  onLaunched?: () => void | Promise<unknown>
) => {
  const [isPlacingOrder, setIsPlacingOrder] = useState(false)
  const [hasCostChanged, setHasCostChanged] = useState(false)
  const [failedToLaunch, setFailedToLaunch] = useState(false)
  const [launchErrorDetails, setLaunchErrorDetails] = useState<
    string | undefined
  >()
  const [isOrderPlaced, setIsOrderPlaced] = useState(false)

  const { data: fieldworkData, loading: loadingFieldwork } = useQuery(
    GET_FIELDWORK,
    {
      context: { clientName: 'questionnaire' },
      variables: { surveyId },
      onError: (error) => {
        captureApolloError(LoggerErrorType.ApolloQuery, 'fieldwork', error)
      }
    }
  )
  const fieldwork = fieldworkData?.fieldwork

  const panelSupplierCode = fieldwork?.audience[0]?.__typename
    ? convertAudienceType(fieldwork.audience[0].__typename)
    : null
  const estimatedLengthOfInterview =
    fieldwork?.lengthOfInterview.unadjusted || 5
  const wantedCompletes = fieldwork?.samplesNeeded.unadjusted
  const fieldworkDataStartDate = fieldwork?.duration.startDate

  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  useEffect(() => {
    if (!fieldworkDataStartDate) return
    setSelectedDate(
      new Date(
        getStartAndEndDatesAdjusted(
          fieldworkDataStartDate,
          DEFAULT_ESTIMATED_DELIVERY_TIME
        ).adjustedStartDate
      )
    )
  }, [fieldworkDataStartDate])

  // Cost quote uses the real LOI; mirrors usePollCostQuote (no-cache + poll).
  const {
    data: costData,
    loading: loadingCost,
    refetch: refetchCost
  } = useQuery(GET_COST_QUOTE, {
    context: { clientName: 'questionnaire' },
    variables: {
      projectId,
      surveyId,
      lengthOfInterview: estimatedLengthOfInterview
    },
    fetchPolicy: 'no-cache',
    skip: !fieldwork,
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloQuery, 'getCostQuote', error)
    }
  })
  const costPerIncidence = costData?.getCostQuote?.cpi.amount ?? 0
  const baseAmount = costData?.getCostQuote?.cpi.baseAmount ?? 0
  const roundedCostPerIncidence = Number.parseFloat(costPerIncidence.toFixed(2))

  // Feasibility uses a fixed 5-min LOI, matching useGetFeasibilityEstimateData;
  // poll until the server finishes computing it.
  const { data: feasibilityData, loading: loadingFeasibility } = useQuery(
    GET_FEASIBILITY_ESTIMATE,
    {
      context: { clientName: 'questionnaire' },
      variables: {
        projectId,
        surveyId,
        lengthOfInterview: DEFAULT_FEASIBILITY_LENGTH_OF_INTERVIEW
      },
      pollInterval: FEASIBILITY_POLL_INTERVAL,
      onError: (error) => {
        captureApolloError(
          LoggerErrorType.ApolloQuery,
          'getFeasibilityEstimate',
          error
        )
      }
    }
  )
  const feasibilityEstimate = feasibilityData?.getFeasibilityEstimate
  const isFeasible = feasibilityEstimate?.isFeasible
  const feasibility = feasibilityEstimate?.feasibility ?? undefined
  const estimatedDeliveryDays =
    feasibilityEstimate?.estimatedDeliveryTime ??
    DEFAULT_ESTIMATED_DELIVERY_TIME

  const totalCost =
    roundedCostPerIncidence && wantedCompletes
      ? calculateTotalCost(roundedCostPerIncidence, wantedCompletes)
      : undefined

  const { formattedEndDate, formattedStartDate } = getStartAndEndDatesFormatted(
    selectedDate,
    estimatedDeliveryDays
  )
  const isIncrementalFieldwork =
    fieldwork?.samplesNeededIncreaseFrequency !== null &&
    fieldwork?.samplesNeededIncreaseFrequency !== undefined
  const completionDate = getMaxDateBetweenDefaultAndEndDate(
    isIncrementalFieldwork,
    formattedEndDate,
    fieldwork?.duration.endDate
      ? new Date(fieldwork.duration.endDate)
      : undefined
  )
  const maxEstimatedDeliveryDays = getMaxEstimatedDeliveryDays(
    isIncrementalFieldwork,
    estimatedDeliveryDays,
    fieldwork?.duration.endDate
      ? new Date(fieldwork.duration.endDate)
      : undefined
  )

  const { updateFieldworkV2 } = useUpdateFieldworkV2()
  const [createSurveyOrderV2, { loading: loadingCreateSurveyOrderV2 }] =
    useMutation<CreateSurveyOrderData, CreateSurveyOrderV2MutationVariables>(
      CREATE_SURVEY_ORDER_V2,
      {
        context: { clientName: 'dashboard' },
        onCompleted: () => setIsOrderPlaced(true),
        onError: (error) => {
          setFailedToLaunch(true)
          setLaunchErrorDetails(getLaunchErrorDetails(error))
          captureApolloError(
            LoggerErrorType.ApolloMutation,
            'createSurveyOrder',
            error
          )
        }
      }
    )

  const placeOrder = async () => {
    setIsPlacingOrder(true)
    setFailedToLaunch(false)
    setLaunchErrorDetails(undefined)
    try {
      if (feasibility !== undefined && costPerIncidence && panelSupplierCode) {
        // Re-check the cost quote right before launching so a silently-changed
        // cost surfaces instead of being charged.
        const { data: newCostDetails } = await refetchCost({
          projectId,
          surveyId,
          lengthOfInterview: estimatedLengthOfInterview
        })
        const newCostPerIncidence = newCostDetails.getCostQuote?.cpi.amount
        setHasCostChanged(costPerIncidence !== newCostPerIncidence)

        if (costPerIncidence === newCostPerIncidence) {
          const { adjustedStartDate, adjustedEndDate } =
            getStartAndEndDatesAdjusted(selectedDate, maxEstimatedDeliveryDays)
          await updateFieldworkV2({
            variables: {
              input: {
                surveyId,
                panelSupplierCode,
                lengthOfInterview: estimatedLengthOfInterview,
                startDate: adjustedStartDate,
                endDate: adjustedEndDate,
                countryCode: null,
                samplesNeeded: null
              }
            }
          })

          const setSurveyLive = getSurveyLiveConfig()
          await createSurveyOrderV2({
            variables: {
              surveyId,
              feasibility,
              customerCPI: costPerIncidence,
              baseCPI: baseAmount,
              panelSupplierCode,
              ...(setSurveyLive !== undefined && { setSurveyLive })
            }
          })
          await onLaunched?.()
        }
      }
    } catch (error) {
      setFailedToLaunch(true)
      setLaunchErrorDetails(getLaunchErrorDetails(error))
    } finally {
      setIsPlacingOrder(false)
    }
  }

  const shouldIgnoreFeasibility =
    localStorage.getItem('ignoreFeasibility') === 'true'
  const loadingData = loadingFieldwork || loadingCost || loadingFeasibility

  const summary: MarketLaunchSummary = {
    goLiveDate: formattedStartDate,
    estimatedDeliveryDays: maxEstimatedDeliveryDays,
    completionDate,
    costPerComplete: costPerIncidence || undefined,
    completes: wantedCompletes,
    totalCost
  }

  return {
    summary,
    // Panel CPI — misleading on an own-audience market. Same gate as the
    // single-survey Confirmation page.
    showCostBreakdown:
      panelSupplierCode === PanelSupplier.CINT ||
      panelSupplierCode === PanelSupplier.PURESPECTRUM,
    canLaunch:
      (shouldIgnoreFeasibility || !!isFeasible) &&
      !loadingData &&
      !!costPerIncidence,
    placeOrder,
    isPlacingOrder: isPlacingOrder || loadingCreateSurveyOrderV2,
    loadingData,
    isOrderPlaced,
    hasCostChanged,
    failedToLaunch,
    launchErrorDetails
  }
}
